import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Save, Link as LinkIcon } from 'lucide-react';
import UserSelect from '../components/UserSelect';
import { useTranslation } from '../context/LanguageContext';
import { usePermission } from '../context/PermissionContext';

const TaskForm = ({ viewOnly }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reference_link: '',
    status: 'pending',
    assignee_id: '',
    department_id: '',
    created_by: '',
  });
  const [assignees, setAssignees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load departments
    fetch('/api/organization/departments')
      .then(r => r.json())
      .then(data => { if (data.status === 'success') setDepartments(data.data); })
      .catch(console.error);

    if (isEdit) {
      setLoading(true);
      fetch(`/api/tasks/view/${id}`)
        .then(r => r.json())
        .then(data => {
          if (data.status === 'success') {
            const task = data.data;
            setFormData({
              name: task.name || '',
              description: task.description || '',
              reference_link: task.reference_link || '',
              status: task.status || 'pending',
              assignee_id: task.assignee_id ? String(task.assignee_id) : '',
              department_id: task.department_id ? String(task.department_id) : '',
              created_by: task.created_by || '',
              creator_name: task.creator_name || '',
              assignee_name: task.assignee_name || '',
              department_name: task.department_name || '',
              created_at: task.created_at || '',
              updated_at: task.updated_at || '',
            });
            setAssignees(task.assignees || []);
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(t('tasks.name_required'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        reference_link: formData.reference_link,
        status: formData.status,
        department_id: formData.department_id ? parseInt(formData.department_id, 10) : null,
        assignee_id: formData.assignee_id ? parseInt(formData.assignee_id, 10) : null,
        assignees,
      };

      let url = '/api/tasks/add';
      let method = 'POST';
      if (isEdit) {
        url = `/api/tasks/edit/${id}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.status === 'success') {
        navigate(isEdit ? `/tasks/view/${id}` : '/tasks');
      } else {
        alert(data.message || t('common.save_failed'));
      }
    } catch (e) {
      alert(t('common.save_failed'));
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="text-center p-10">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {viewOnly ? t('tasks.view_title') : isEdit ? t('tasks.edit_title') : t('tasks.create_title')}
        </h1>
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
        {/* Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('tasks.name')} <span className="text-red-500">*</span>
          </label>
          {viewOnly ? (
            <p className="text-slate-800 dark:text-slate-200">{formData.name}</p>
          ) : (
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent"
              required
            />
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('tasks.description')}
          </label>
          {viewOnly ? (
            <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{formData.description || '-'}</p>
          ) : (
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={4}
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent resize-y"
            />
          )}
        </div>

        {/* Reference Link */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('tasks.reference_link')}
          </label>
          {viewOnly ? (
            formData.reference_link ? (
              <a href={formData.reference_link} target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 inline-flex items-center gap-1">
                <LinkIcon size={14} />
                {formData.reference_link}
              </a>
            ) : (
              <p className="text-slate-400">-</p>
            )
          ) : (
            <input
              type="url"
              value={formData.reference_link}
              onChange={e => setFormData({...formData, reference_link: e.target.value})}
              placeholder="https://..."
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent"
            />
          )}
        </div>

        {/* Status - shown only in view mode */}
        {viewOnly && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {t('tasks.status')}
            </label>
            <p className="text-slate-800 dark:text-slate-200">
              {formData.status === 'completed' ? t('tasks.completed') : formData.status === 'processing' ? t('tasks.processing') : t('tasks.pending')}
            </p>
          </div>
        )}

        {/* Department */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('tasks.department')}
          </label>
          {viewOnly ? (
            <p className="text-slate-800 dark:text-slate-200">{formData.department_name || '-'}</p>
          ) : (
            <select
              value={formData.department_id}
              onChange={e => setFormData({...formData, department_id: e.target.value})}
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent"
            >
              <option value="">{t('common.select_placeholder')}</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Assignee (direct select) */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('tasks.assignee')}
          </label>
          {viewOnly ? (
            <p className="text-slate-800 dark:text-slate-200">{formData.assignee_name ? `${formData.assignee_id} - ${formData.assignee_name.replace(/\s/g, '')}` : '-'}</p>
          ) : (
            <UserSelect
              value={formData.assignee_id}
              onChange={val => setFormData({...formData, assignee_id: val})}
            />
          )}
        </div>

        {/* Creator (view only) */}
        {viewOnly && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('tasks.creator')}</label>
              <p className="text-slate-800 dark:text-slate-200 text-sm">{formData.creator_name ? `${formData.created_by || ''} - ${formData.creator_name.replace(/\s/g, '')}` : '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('tasks.created_at')}</label>
              <p className="text-slate-800 dark:text-slate-200 text-sm">{(formData.created_at || '').substring(0, 19).replace('T', ' ')}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('tasks.updated_at')}</label>
              <p className="text-slate-800 dark:text-slate-200 text-sm">{(formData.updated_at || '').substring(0, 19).replace('T', ' ')}</p>
            </div>
          </>
        )}

        {/* Actions */}
        {!viewOnly && (
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                saving
                  ? 'bg-blue-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <Save size={18} />
              )}
              {saving ? t('common.processing') : (isEdit ? t('common.update') : t('common.add'))}
            </button>
          </div>
        )}

        {viewOnly && (
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              {t('common.back')}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default TaskForm;
